import type { EvinAnalysisResult } from '../../shared/types';

/* ── Inline formatting ──────────────────────────────────── */

export function esc(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

export function inlineMd(text: string): string {
  return esc(text)
    .replace(/\*\*\*(.+?)\*\*\*/g, '<strong><em>$1</em></strong>')
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>');
}

export function mdToHtml(md: string): string {
  const thStyle = 'text-align:left;font-weight:600;padding:8px 12px;border-bottom:2px solid #e2e8f0;color:#1e293b;';
  const tdStyle = 'text-align:left;padding:8px 12px;border-bottom:1px solid #f1f5f9;vertical-align:top;color:#475569;';

  // Extract tables
  const escaped = esc(md);
  const lines = escaped.split('\n');
  const tables: string[] = [];
  const processed: string[] = [];
  let i = 0;

  while (i < lines.length) {
    const ln = lines[i].trim();
    if (ln.startsWith('|') && ln.endsWith('|') && i + 1 < lines.length) {
      const sep = lines[i + 1]?.trim() ?? '';
      if (sep.startsWith('|') && /^[\s|:-]+$/.test(sep)) {
        const hdrs = ln.slice(1, -1).split('|').map(c => c.trim());
        let t = `<table style="width:100%;border-collapse:collapse;margin:16px 0;font-size:13px;"><thead><tr>`;
        hdrs.forEach(h => { t += `<th style="${thStyle}">${h}</th>`; });
        t += '</tr></thead><tbody>';
        i += 2;
        while (i < lines.length) {
          const rl = lines[i].trim();
          if (!rl.startsWith('|') || !rl.endsWith('|')) break;
          const cells = rl.slice(1, -1).split('|').map(c => c.trim());
          t += '<tr>';
          cells.forEach(c => { t += `<td style="${tdStyle}">${c}</td>`; });
          t += '</tr>';
          i++;
        }
        t += '</tbody></table>';
        const ph = `__TBL${tables.length}__`;
        tables.push(t);
        processed.push(ph);
        continue;
      }
    }
    processed.push(lines[i]);
    i++;
  }

  let html = processed.join('\n')
    .replace(/^#### (.+)$/gm, '<h4 style="font-size:14px;font-weight:600;margin:20px 0 8px;color:#1e293b;">$1</h4>')
    .replace(/^### (.+)$/gm, '<h3 style="font-size:16px;font-weight:600;margin:24px 0 10px;color:#1e293b;">$1</h3>')
    .replace(/^## (.+)$/gm, '<h2 style="font-size:18px;font-weight:600;margin:28px 0 12px;color:#1e293b;">$1</h2>')
    .replace(/^# (.+)$/gm, '<h1 style="font-size:22px;font-weight:600;margin:32px 0 14px;color:#0f172a;">$1</h1>')
    .replace(/\*\*\*(.+?)\*\*\*/g, '<strong><em>$1</em></strong>')
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    .replace(/^---$/gm, '<hr style="border:none;border-top:1px solid #e2e8f0;margin:24px 0;">')
    .replace(/^- (.+)$/gm, '<li style="margin:6px 0;line-height:1.7;">$1</li>')
    .replace(/\n\n/g, '</p><p style="margin:14px 0;line-height:1.7;color:#475569;">')
    .replace(/\n/g, '<br>');

  html = html.replace(/(<li[^>]*>.*?<\/li>(?:<br>)?)+/g, (m) =>
    '<ul style="padding-left:20px;margin:14px 0;">' + m.replace(/<br>/g, '') + '</ul>',
  );

  tables.forEach((t, idx) => { html = html.replace(`__TBL${idx}__`, t); });
  return html;
}

/* ── Section HTML builders ──────────────────────────────── */

function sectionBlock(title: string, borderColor: string, bgColor: string, titleColor: string, content: string): string {
  return `
    <div style="border-left:4px solid ${borderColor};background:${bgColor};border-radius:8px;padding:20px 24px;margin:24px 0;">
      <h3 style="font-size:12px;font-weight:600;text-transform:uppercase;letter-spacing:1px;color:${titleColor};margin:0 0 16px 0;">${esc(title)}</h3>
      ${content}
    </div>`;
}

function itemBlock(element: string, explanation: string, ref: string, refColor: string, extra = ''): string {
  return `
    <div style="margin-bottom:20px;">
      <p style="font-weight:600;color:#1e293b;margin:0 0 6px 0;">${inlineMd(element)}</p>
      <p style="color:#475569;line-height:1.7;margin:0 0 6px 0;">${inlineMd(explanation)}</p>
      ${extra}
      <p style="font-size:12px;color:${refColor};margin:4px 0 0 0;">Ref: ${esc(ref)}</p>
    </div>`;
}

/* ── Main export ────────────────────────────────────────── */

export function buildAnalysisEmailHtml(result: EvinAnalysisResult): string {
  const isQuestion = !!result.generalAnswer;

  let body = '';

  if (isQuestion) {
    // General question — full markdown answer
    body += `<div style="background:#f8fafc;border:1px solid #e2e8f0;border-radius:8px;padding:28px 32px;margin:20px 0;">`;
    body += `<div style="font-size:14px;color:#334155;line-height:1.8;">${mdToHtml(result.generalAnswer!)}</div>`;
    body += `</div>`;

    if (result.summary) {
      body += `<div style="margin:20px 0;font-size:14px;color:#64748b;font-style:italic;line-height:1.7;">${mdToHtml(result.summary)}</div>`;
    }
  } else {
    // Scored analysis — score + verdict
    const scoreColor = result.score >= 80 ? '#10b981' : result.score >= 60 ? '#eab308' : result.score >= 40 ? '#f97316' : '#ef4444';
    const verdictLabels: Record<string, string> = { CONFORME: 'Conforme', VIGILANCE: 'Vigilance', RISQUE_ELEVE: 'Risque élevé', NON_CONFORME: 'Non conforme' };
    const verdictColors: Record<string, string> = { CONFORME: '#10b981', VIGILANCE: '#eab308', RISQUE_ELEVE: '#f97316', NON_CONFORME: '#ef4444' };
    const verdictBg: Record<string, string> = { CONFORME: '#ecfdf5', VIGILANCE: '#fefce8', RISQUE_ELEVE: '#fff7ed', NON_CONFORME: '#fef2f2' };

    body += `
      <div style="text-align:center;padding:24px 0 20px;">
        <div style="font-size:48px;font-weight:700;color:${scoreColor};">${result.score}<span style="font-size:18px;font-weight:400;color:#94a3b8;">/100</span></div>
        <div style="display:inline-block;padding:4px 16px;border-radius:20px;font-size:14px;font-weight:600;color:${verdictColors[result.verdict]};background:${verdictBg[result.verdict]};margin-top:8px;">
          ${verdictLabels[result.verdict] ?? result.verdict}
        </div>
      </div>`;

    if (result.summary) {
      body += `<div style="text-align:center;margin:12px auto 24px;max-width:520px;font-size:14px;color:#64748b;line-height:1.7;">${mdToHtml(result.summary)}</div>`;
    }

    // Judge scores
    const defWins = result.judgeDefenseScore >= result.judgeContradicteurScore;
    body += `
      <div style="text-align:center;margin:16px 0 32px;font-size:14px;color:#64748b;">
        <span style="color:${defWins ? '#10b981' : '#94a3b8'};font-weight:${defWins ? '600' : '400'};">Défense : ${result.judgeDefenseScore}/100</span>
        &nbsp;&nbsp;|&nbsp;&nbsp;
        <span style="color:${!defWins ? '#ef4444' : '#94a3b8'};font-weight:${!defWins ? '600' : '400'};">Contradicteur : ${result.judgeContradicteurScore}/100</span>
      </div>`;
  }

  // Interdictions
  if (result.interdictionsFormelles.length > 0) {
    const items = result.interdictionsFormelles.map(it =>
      itemBlock(it.element, it.explanation, it.legalBasis, '#dc2626'),
    ).join('');
    body += sectionBlock('Interdictions formelles', '#ef4444', '#fef2f2', '#dc2626', items);
  }

  // Points de vigilance
  if (result.pointsDeVigilance.length > 0) {
    const riskColors: Record<string, string> = { 'Faible': '#ca8a04', 'Moyen': '#ea580c', 'Élevé': '#dc2626' };
    const riskBg: Record<string, string> = { 'Faible': '#fefce8', 'Moyen': '#fff7ed', 'Élevé': '#fef2f2' };
    const items = result.pointsDeVigilance.map(it => {
      const badge = `<span style="display:inline-block;padding:2px 8px;border-radius:4px;font-size:11px;font-weight:500;color:${riskColors[it.riskLevel] ?? '#94a3b8'};background:${riskBg[it.riskLevel] ?? '#f8fafc'};margin-left:8px;">${esc(it.riskLevel)}</span>`;
      const reco = it.recommendation
        ? `<p style="font-size:12px;color:#7c3aed;margin:8px 0 0;line-height:1.6;">Recommandation : ${inlineMd(it.recommendation)}</p>`
        : '';
      return `
        <div style="margin-bottom:20px;">
          <p style="font-weight:600;color:#1e293b;margin:0 0 6px 0;">${inlineMd(it.element)}${badge}</p>
          <p style="color:#475569;line-height:1.7;margin:0 0 6px 0;">${inlineMd(it.explanation)}</p>
          ${reco}
          <p style="font-size:12px;color:#92400e;margin:4px 0 0 0;">Ref: ${esc(it.legalBasis)}</p>
        </div>`;
    }).join('');
    body += sectionBlock('Points de vigilance', '#f59e0b', '#fffbeb', '#92400e', items);
  }

  // Points conformes
  if (result.conformePoints.length > 0) {
    const items = result.conformePoints.map(it =>
      itemBlock(it.element, it.explanation, it.legalBasis, '#059669'),
    ).join('');
    body += sectionBlock('Points conformes', '#10b981', '#ecfdf5', '#059669', items);
  }

  // Sources
  if (result.sources.length > 0) {
    const srcItems = result.sources.map(s => {
      const typeColors: Record<string, string> = { LOI: '#2563eb', JURISPRUDENCE: '#7c3aed', ARPP: '#475569', DOCTRINE: '#475569' };
      return `<div style="margin-bottom:8px;font-size:12px;line-height:1.6;">
        <span style="display:inline-block;padding:1px 6px;border-radius:3px;font-weight:500;color:${typeColors[s.type] ?? '#475569'};background:#f1f5f9;font-size:11px;margin-right:6px;">${esc(s.type)}</span>
        <span style="color:#64748b;">${esc(s.title)} — ${esc(s.reference)}${s.date ? ` (${esc(s.date)})` : ''}</span>
      </div>`;
    }).join('');
    body += sectionBlock('Sources juridiques', '#94a3b8', '#f8fafc', '#475569', srcItems);
  }

  // Arbitrage
  if (result.arbitrageDetails) {
    body += `
      <div style="border:1px solid #e2e8f0;border-radius:8px;padding:20px 24px;margin:24px 0;">
        <h3 style="font-size:12px;font-weight:600;text-transform:uppercase;letter-spacing:1px;color:#475569;margin:0 0 16px 0;">Détails de l'arbitrage</h3>
        <div style="font-size:13px;color:#64748b;line-height:1.7;">${mdToHtml(result.arbitrageDetails)}</div>
      </div>`;
  }

  // Full newsletter template
  return `<!DOCTYPE html>
<html><head><meta charset="utf-8"></head>
<body style="margin:0;padding:0;background:#f1f5f9;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,'Helvetica Neue',sans-serif;">
  <div style="max-width:640px;margin:0 auto;background:#ffffff;border-radius:8px;overflow:hidden;margin-top:20px;margin-bottom:20px;box-shadow:0 1px 3px rgba(0,0,0,0.1);">
    <!-- Header -->
    <div style="background:linear-gradient(135deg,#7c3aed,#4f46e5);padding:24px 32px;">
      <table style="width:100%;"><tr>
        <td><span style="font-size:18px;font-weight:700;color:#ffffff;letter-spacing:-0.5px;">Evinly</span></td>
        <td style="text-align:right;"><span style="font-size:11px;color:rgba(255,255,255,0.7);text-transform:uppercase;letter-spacing:1px;">Analyse Loi Évin</span></td>
      </tr></table>
    </div>

    <!-- Body -->
    <div style="padding:32px;">
      ${body}
    </div>

    <!-- Footer -->
    <div style="background:#f8fafc;border-top:1px solid #e2e8f0;padding:20px 32px;">
      <p style="font-size:11px;color:#94a3b8;line-height:1.6;margin:0;text-align:center;">
        Cette analyse ne constitue pas un avis juridique. Consultez un avocat spécialisé pour une validation officielle.<br>
        Données juridiques : DILA / Légifrance — Licence Ouverte 2.0
      </p>
      <p style="font-size:10px;color:#cbd5e1;margin:8px 0 0;text-align:center;">
        Généré par Evinly — loievin.fr
      </p>
    </div>
  </div>
</body></html>`;
}
